import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './index.module.scss';

interface IProps {
	setIDsRestaurant: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	IDSelectedCustomer: Nullable<string>;
	IDsSelectedRestaurant: string[];
}

export const RestaurantsMultipleSelect: React.FC<IProps> = React.memo(
	({ setIDsRestaurant, setFetchedRestaurants, IDSelectedCustomer, IDsSelectedRestaurant }) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);
		const { response, loading, refetch, responseError } = useFetchData<
			IGetRestaurantsShort,
			IGetRestaurantsShortArgs
		>(IDSelectedCustomer && hasAccess ? getRestaurantsShortInfo : null, {
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v2',
		});

		const restaurants = useMemo(() => {
			return hasAccess ? response?.data.restaurants : loggedUser.restaurants;
		}, [hasAccess, response, loggedUser]);

		const handleRemove = useCallback(
			(id: string) => {
				const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
				setIDsRestaurant(updatedSelectedItems);
			},
			[IDsSelectedRestaurant, setIDsRestaurant],
		);

		const restaurantsList = useMemo(() => {
			return IDsSelectedRestaurant.map((el) => {
				const label = restaurants?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
			});
		}, [IDsSelectedRestaurant, restaurants, handleRemove]);

		useEffect(() => {
			refetch();
		}, [IDSelectedCustomer]);

		useEffect(() => {
			if (response) {
				setFetchedRestaurants(response.data.restaurants);
			} else if (!hasAccess) {
				const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
				setFetchedRestaurants(accessibleRestaurants);
			}
		}, [response, loggedUser, hasAccess, IDSelectedCustomer]);

		return (
			<div className={style.container} data-test="RestaurantsMultipleSelect">
				<div className={style.field}>
					<MultipleSelect
						data={restaurants || []}
						isLoading={loading}
						refetch={refetch}
						responseError={responseError}
						setIDsRestaurant={setIDsRestaurant}
						IDSelectedCustomer={IDSelectedCustomer}
						IDsSelectedRestaurant={IDsSelectedRestaurant}
					/>
				</div>
				<div className={style.groupsList}>{restaurantsList}</div>
			</div>
		);
	},
);
